import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/dateUtils';
import { getPauseSubTypeLabel, type I18nLike } from '@/types/changeRequest';
import type { change_requests_PausePlanResponse } from '@/api';

interface PauseBillingDeltaProps {
  plan: change_requests_PausePlanResponse;
  i18n: I18nLike;
}

function DeltaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/**
 * PauseBillingDelta renders the read-only billing-delta preview for a bounded
 * contract pause (#784): the [pause_from, pause_to] window, the create/extend/
 * cancel sub-type, and the invoices the window will affect. Read-only summary in
 * semantic markup (no raw <table>); status is conveyed by text, not colour alone.
 * Mirrors CareDayBillingDelta.
 */
export function PauseBillingDelta({ plan, i18n }: PauseBillingDeltaProps) {
  const affected = plan.affected_invoices ?? [];

  const actionLabel = (action: string | undefined): string => {
    switch (action) {
      case 'cancel':
        return i18n._({ id: 'pause.plan.action.cancel', message: 'Cancel' });
      case 'reissue':
        return i18n._({ id: 'pause.plan.action.reissue', message: 'Re-issue' });
      default:
        return action ?? '';
    }
  };

  return (
    <div className="space-y-4 rounded-md border bg-muted/30 p-3">
      <DeltaRow label={i18n._({ id: 'pause.plan.window', message: 'Pause window' })}>
        <p className="text-sm">
          {formatDate(plan.pause_from)} – {formatDate(plan.pause_to)}
        </p>
      </DeltaRow>

      <DeltaRow label={i18n._({ id: 'pause.plan.subType.label', message: 'Change' })}>
        <p className="text-sm">{getPauseSubTypeLabel(i18n, plan.sub_type)}</p>
      </DeltaRow>

      {plan.no_change ? (
        <p className="text-sm text-warning-on-surface">
          {i18n._({
            id: 'pause.plan.noChange',
            message: 'No billing change — the pause is already as requested.',
          })}
        </p>
      ) : (
        <DeltaRow label={i18n._({ id: 'pause.plan.affectedInvoices', message: 'Affected invoices' })}>
          {affected.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {i18n._({ id: 'pause.plan.noAffectedInvoices', message: 'No existing invoices affected; the period will be (re)generated.' })}
            </p>
          ) : (
            <ul className="text-sm space-y-1">
              {affected.map((inv, idx) => (
                <li key={`${inv.invoice_id ?? 'inv'}-${idx}`} className="flex items-center gap-2">
                  <span className="font-medium">
                    {inv.period_year}-{String(inv.period_month ?? 0).padStart(2, '0')}
                  </span>
                  <Badge variant="outline">{inv.status}</Badge>
                  <Badge variant={inv.action === 'cancel' ? 'destructive' : 'secondary'}>
                    {actionLabel(inv.action)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </DeltaRow>
      )}
    </div>
  );
}
